/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import React, { ReactElement } from "react";

import { useHistory } from "react-router-dom";

import { ActionButton, Pivot, PivotItem, Spinner, SpinnerSize, Stack } from "@fluentui/react";

import ProfileHeader from "Components/ProfileHeader/ProfileHeader";
import RegionalDirectorBenefitDetails from "Components/RegionalDirectorBenefitDetails/RegionalDirectorBenefitDetails";
import { RegionalDirectorComments } from "Components/RegionalDirectorComments/RegionalDirectorComments";
import RegionalDirectorProfile from "Components/RegionalDirectorProfile/RegionalDirectorProfile";
import RegionalDirectorProgramProfile from "Components/RegionalDirectorProgramProfile/RegionalDirectorProgramProfile";
import RegionalDirectorsDistributionLists from "Components/RegionalDirectorsDistributionLists/RegionalDirectorsDistributionLists";
import { ProfileTabs } from "Constants/ProfileConstants";
import { TenantTypesEnum } from "Constants/TenantTypes";
import { DetailWrapper } from "Containers/Common/CommonContainer.styles";
import {
  containerStackTokens,
  wrapperStackStyles,
} from "Containers/MVP/MostValuableProfessionalDetailsContainer/MostValuableProfessionalDetailsContainer.styles";
import { useProfileTabs } from "Utility/CustomTabHooks";

import { IRegionalDirectorDetailsProps } from "./IRegionalDirectorDetailsProps";

/**
 * The regional director details component.
 * @param {IRegionalDirectorDetailsProps} props The props.
 */
export const RegionalDirectorDetails = (props: IRegionalDirectorDetailsProps): ReactElement => {
  const history = useHistory();

  const {
    isCurrentUserProfileLoading,
    selectedUserProfile,
    userProfileId,
    selectedUserProfileErrorMessage,
    fetchUserProfileComments,
    userProfileCommentsErrorMessage,
    userProfileComments,
    isUserProfileCommentsLoading,
    onAddCommentClicked,
    addUserProfileCommentStatus,
    canUpdatePrimaryEmail,
    updatePrimaryEmailStatus,
    updatePrimaryEmailError,
    onPrimaryEmailUpdate,
    onPrimaryEmailUpdateReset,
    loadUserProfile,
  } = props;

  const { selectedTab, setSelectedTab } = useProfileTabs();

  /**
   * Handler for when the back button is clicked.
   * @returns {void} void no return for function.
   */
  const onBackClickEvent = (): void => {
    history.goBack();
  };

  if (isCurrentUserProfileLoading) {
    return (
      <Spinner
        size={SpinnerSize.large}
        styles={{
          root: {
            marginTop: 32,
          },
        }}
      />
    );
  }

  if (!!selectedUserProfileErrorMessage) {
    return <>{selectedUserProfileErrorMessage}</>;
  }

  return (
    <>
      <Stack styles={wrapperStackStyles}>
        <ActionButton
          iconProps={{ iconName: "RevToggleKey" }}
          ariaLabel="Back"
          onClick={onBackClickEvent}
        >
          Back
        </ActionButton>
        <Stack tokens={containerStackTokens}>
          <ProfileHeader
            userProfileId={selectedUserProfile.id}
            firstName={selectedUserProfile.firstName}
            lastName={selectedUserProfile.lastName}
            primaryEmail={selectedUserProfile.primaryEmail}
            secondaryEmail={selectedUserProfile.secondaryEmail}
            tenants={selectedUserProfile.tenants}
            currentTenant={TenantTypesEnum.RD}
            isOvdfBlock={false}
            isPrivate={selectedUserProfile?.isPrivate}
            canUpdatePrimaryEmail={canUpdatePrimaryEmail}
            updatePrimaryEmailStatus={updatePrimaryEmailStatus}
            updatePrimaryEmailError={updatePrimaryEmailError}
            onPrimaryEmailUpdate={onPrimaryEmailUpdate}
            onPrimaryEmailUpdateReset={onPrimaryEmailUpdateReset}
            loadUserProfile={loadUserProfile}
          />
          <Stack>
            <DetailWrapper>
              <Pivot
                selectedKey={selectedTab}
                onLinkClick={(item: PivotItem | undefined): void => {
                  setSelectedTab((item?.props.itemKey as ProfileTabs) || ProfileTabs.Profile);
                }}
              >
                <PivotItem headerText="Profile" itemKey={ProfileTabs.Profile}>
                  <RegionalDirectorProfile />
                </PivotItem>
                <PivotItem
                  headerText="Program Information"
                  itemKey={ProfileTabs.ProgramInformation}
                >
                  <RegionalDirectorProgramProfile
                    userProfileId={selectedUserProfile.id}
                    userProfileAwardPeriodEndDate={selectedUserProfile.awardPeriodEndDate}
                  />
                </PivotItem>
                <PivotItem headerText="Benefits" itemKey={ProfileTabs.Benefits}>
                  <RegionalDirectorBenefitDetails />
                </PivotItem>
                <PivotItem headerText="Distribution Lists" itemKey={ProfileTabs.DistributionLists}>
                  <RegionalDirectorsDistributionLists />
                </PivotItem>
                <PivotItem headerText="Comments" itemKey={ProfileTabs.Comments}>
                  <RegionalDirectorComments
                    onAddCommentClicked={onAddCommentClicked}
                    isLoading={isUserProfileCommentsLoading}
                    userProfileId={userProfileId}
                    fetchUserProfileComments={fetchUserProfileComments}
                    userProfileComments={userProfileComments}
                    errorMessage={userProfileCommentsErrorMessage}
                    addCommentStatus={addUserProfileCommentStatus}
                  />
                </PivotItem>
              </Pivot>
            </DetailWrapper>
          </Stack>
        </Stack>
      </Stack>
    </>
  );
};
